/**
 * Scoring Engine
 * 
 * Handles dynamic scoring calculations for the Trilogy assessment system.
 * Computes question contributions, pillar totals, normalized scores, and zone determination.
 */

/**
 * Calculates a question's contribution to the total score
 * @param {number} answerValue - User's answer (1-5 typically)
 * @param {number} weight - Question weight (default 1)
 * @returns {number} Contribution to score
 */
export function calculateContribution(answerValue, weight = 1) {
  return answerValue * weight;
}

/**
 * Computes pillar totals from answers
 * @param {Object} answers - Answer object {questionKey: value}
 * @param {Array} questions - Array of question objects
 * @returns {Object} Pillar totals {LEAD: x, EVOLVE: y, ...}
 */
export function computePillarTotals(answers, questions) {
  const totals = {
    LEAD: 0,
    EVOLVE: 0,
    ALIGN: 0,
    PERFORM: 0
  };

  questions.forEach(q => {
    if (q.type === 'scored' && answers[q.key] !== undefined) {
      const contribution = calculateContribution(
        answers[q.key],
        q.weight || 1
      );
      totals[q.pillar] = (totals[q.pillar] || 0) + contribution;
    }
  });

  return totals;
}

/**
 * Normalizes raw score to 0-75 scale
 * @param {number} rawScore - Raw total score
 * @param {number} maxPossibleScore - Maximum possible score
 * @returns {number} Normalized score (0-75)
 */
export function normalizeScore(rawScore, maxPossibleScore) {
  if (maxPossibleScore === 0) return 0;
  return Math.round((rawScore / maxPossibleScore) * 75);
}

/**
 * Determines zone based on normalized score
 * @param {number} normalizedScore - Score on 0-75 scale
 * @returns {string} Zone: RED, YELLOW, or GREEN
 */
export function determineZone(normalizedScore) {
  if (normalizedScore <= 30) return 'RED';
  if (normalizedScore <= 55) return 'YELLOW';
  return 'GREEN';
}

/**
 * Computes complete scan results
 * @param {Object} answers - User answers
 * @param {Array} questions - Question set
 * @param {number} maxPossibleScore - Maximum possible score
 * @returns {Object} Complete scoring results
 */
export function computeScanResults(answers, questions, maxPossibleScore) {
  // Compute pillar totals
  const rawScores = computePillarTotals(answers, questions);
  
  // Compute total raw score
  const totalRaw = Object.values(rawScores).reduce((sum, score) => sum + score, 0);
  
  // Normalize to 0-75 scale
  const normalizedTotal = normalizeScore(totalRaw, maxPossibleScore);
  
  // Determine zone
  const zone = determineZone(normalizedTotal);

  return {
    rawScores,
    totalRaw,
    normalizedTotal,
    zone,
    maxPossibleScore
  };
}
